import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Zap, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import PageHeader from "@/components/PageHeader";
import { createQuickReply, deleteQuickReply, errMsg, listQuickReplies, updateQuickReply } from "@/lib/api";

function Editor({ initial, onSave, onCancel, testPrefix }) {
  const [shortcut, setShortcut] = useState(initial?.shortcut || "");
  const [text, setText] = useState(initial?.text || "");
  return (
    <div className="grid sm:grid-cols-[180px_1fr_auto] gap-3 items-start">
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-600 font-bold">/</span>
        <Input data-testid={`${testPrefix}-shortcut`} value={shortcut} onChange={(e) => setShortcut(e.target.value.replace(/[^a-zA-Z0-9_-]/g, ""))} placeholder="atajo" className="pl-6 bg-white" />
      </div>
      <Textarea data-testid={`${testPrefix}-text`} value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Texto que se insertará en el chat" className="bg-white" />
      <div className="flex gap-1">
        <Button size="icon" data-testid={`${testPrefix}-save`} disabled={!shortcut || !text.trim()} onClick={() => onSave({ shortcut, text })} className="bg-emerald-500 hover:bg-emerald-600"><Check className="h-4 w-4" /></Button>
        {onCancel && <Button size="icon" variant="ghost" data-testid={`${testPrefix}-cancel`} onClick={onCancel}><X className="h-4 w-4" /></Button>}
      </div>
    </div>
  );
}

export default function QuickReplies() {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);

  const load = () => listQuickReplies().then(setItems).catch(() => toast.error("No se pudieron cargar"));
  useEffect(() => { load(); }, []);

  const create = async (body) => {
    try { await createQuickReply(body); toast.success("Respuesta rápida creada"); setAdding(false); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const update = async (id, body) => {
    try { await updateQuickReply(id, body); setEditId(null); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (id) => { await deleteQuickReply(id); load(); };

  return (
    <div className="h-full overflow-y-auto scroll-thin" data-testid="quick-replies-page">
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-10">
        <PageHeader title="Respuestas rápidas" subtitle="Atajos para tus asesores: en el chat escribe / y elige una respuesta para insertarla al instante.">
          <Button data-testid="add-quick-reply-button" onClick={() => setAdding(true)} className="rounded-full bg-emerald-500 hover:bg-emerald-600"><Plus className="h-4 w-4 mr-1.5" />Nueva</Button>
        </PageHeader>
        <div className="space-y-3">
          {adding && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 animate-rise">
              <Editor testPrefix="new-qr" onSave={create} onCancel={() => setAdding(false)} />
            </div>
          )}
          {items.length === 0 && !adding && (
            <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-14 text-center" data-testid="quick-replies-empty">
              <Zap className="h-8 w-8 mx-auto text-slate-300" />
              <p className="mt-3 font-display text-lg font-bold">Sin respuestas rápidas</p>
              <p className="text-sm text-slate-500 mt-1">Crea atajos como /horario o /precios.</p>
            </div>
          )}
          {items.map((q) => (
            <div key={q.id} data-testid={`quick-reply-${q.id}`} className="bg-white border border-slate-200 rounded-2xl p-4 animate-rise">
              {editId === q.id ? (
                <Editor testPrefix={`edit-qr-${q.id}`} initial={q} onSave={(b) => update(q.id, b)} onCancel={() => setEditId(null)} />
              ) : (
                <div className="flex items-start gap-4">
                  <span className="shrink-0 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 font-mono text-sm font-bold">/{q.shortcut}</span>
                  <p className="flex-1 text-sm text-slate-700 whitespace-pre-wrap">{q.text}</p>
                  <Button size="icon" variant="ghost" data-testid={`quick-reply-edit-${q.id}`} onClick={() => setEditId(q.id)}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" data-testid={`quick-reply-delete-${q.id}`} onClick={() => remove(q.id)} className="hover:text-rose-600"><Trash2 className="h-4 w-4" /></Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
